import { TableToolbar, TableToolbarContent, TableToolbarSearch } from "@carbon/react";
import { NullableTableColumns } from "@flaminem/ui-components";
import { toDate } from "@flaminem/utils";
import { FC } from "react";
import { useNavigate } from "react-router-dom";

import { FixedTable } from "src/components/Tables/FixedTable.tsx";
import { defaultPageSize, defaultRowHeight } from "src/config/table-config.ts";
import { useLocalSearch } from "src/hooks/useLocalSearch.ts";
import { useFormatDate, useT } from "src/internationalization";
import { ScenarioVersion, ScenarioVersionWithRules } from "src/types/scenario.ts";

type ArchivedScenarioVersionsTabProps = {
  archivedScenarioVersions: ScenarioVersionWithRules[];
};

export const ArchivedScenarioVersionsTab: FC<ArchivedScenarioVersionsTabProps> = ({
  archivedScenarioVersions,
}) => {
  const navigate = useNavigate();
  const t = useT();
  const formatDate = useFormatDate();
  const { handleSearch, filteredData, searchTerm } = useLocalSearch(
    archivedScenarioVersions ?? [],
    ["name"]
  );

  const columns: NullableTableColumns<ScenarioVersionWithRules> = {
    name: {
      label: t("general.table.name.column.title"),
      render: (scenarioVersion) => scenarioVersion.name,
      width: 100,
      flexBasis: 100,
      flexGrow: 1,
      flexShrink: 1,
    },
    threshold: {
      label: t("scenario.table.header.threshold"),
      render: (scenarioVersion) => scenarioVersion.threshold,
      width: 100,
      flexBasis: 100,
      flexGrow: 1,
      flexShrink: 1,
    },
    rules: {
      label: t("scenario.table.header.rules"),
      render: (scenarioVersion) => scenarioVersion.rules?.length,
      width: 100,
      flexBasis: 100,
      flexGrow: 1,
      flexShrink: 1,
    },
    created: {
      label: t("scenario.table.header.created"),
      render: (scenarioVersion) =>
        formatDate(toDate(scenarioVersion.adminStatus.creation), {
          dateStyle: "medium",
        }),
      width: 100,
      flexBasis: 100,
      flexGrow: 1,
      flexShrink: 1,
    },
    activated: {
      label: t("scenario.table.header.activated"),
      render: (scenarioVersion) =>
        scenarioVersion.activatedAt
          ? formatDate(toDate(scenarioVersion.activatedAt), {
              dateStyle: "medium",
            })
          : null,
      width: 100,
      flexBasis: 100,
      flexGrow: 1,
      flexShrink: 1,
    },
    archived: {
      label: t("scenario.table.header.archived"),
      render: (scenarioVersion) =>
        scenarioVersion.archivedAt
          ? formatDate(toDate(scenarioVersion.archivedAt), {
              dateStyle: "medium",
            })
          : null,
      width: 100,
      flexBasis: 100,
      flexGrow: 1,
      flexShrink: 1,
    },
  };

  const onRowClick = (scenarioVersion: ScenarioVersion) => {
    navigate(`${scenarioVersion.id}`);
  };

  return (
    <FixedTable
      id="archivedScenarioVersions"
      data={filteredData}
      title={t("scenario.tab.archived.table.title")}
      description={t("scenario.tab.archived.table.description")}
      pageSize={defaultPageSize}
      columns={columns}
      rowHeight={defaultRowHeight}
      isLoading={false}
      onRowClick={onRowClick}
      toolbar={() => (
        <TableToolbar aria-label="data table toolbar">
          <TableToolbarContent className="border-b border-solid border-borderSubtle01">
            <TableToolbarSearch
              placeholder={t("scenario.tab.archived.table.search.placeholder")}
              persistent={true}
              onChange={handleSearch}
              value={searchTerm}
            />
          </TableToolbarContent>
        </TableToolbar>
      )}
      filter={(scenarioVersion) =>
        scenarioVersion.name.toLowerCase().includes(searchTerm.toLowerCase())
      }
    />
  );
};
